import {Injectable, Type} from '@angular/core';
import {ConfirmationService, MenuItem, MessageService} from 'primeng/api';
import {finestError} from '@shared/helper/helpers';
import {NavigationBehaviorOptions, NavigationExtras, Router, UrlTree} from '@angular/router';
import {CourseRepository} from '@app/@core/repositories/course.repository';
import {Logger} from '@app/logger.service';
import {BreadcrumbService} from '@app/app.breadcrumb.service';
import {DialogService} from 'primeng/dynamicdialog';
import {MenuDialogComponent, MenuDialogItem} from '@shared/components/menu-dialog.component';
import {DynamicDialogRef} from 'primeng/dynamicdialog/dynamicdialog-ref';
import {DialogBase} from '@shared/helper/dialog-base';
import {InputDialogComponent} from '@shared/components/input-dialog.component';
import {FormlyFieldConfig, FormlyTemplateOptions} from '@ngx-formly/core';
import {ClipboardService} from 'ngx-clipboard';
import {NGXLogger} from 'ngx-logger';
import {Location} from '@angular/common';
import {formly} from '@shared/helper/formly-helpers';
import * as FileSaver from 'file-saver';
import {LeisureShopRepository} from '@app/@core/repositories/leisure-shop.repository';

const log = new Logger();

interface Style {
  'min-width'?: string;
  'min-height'?: string;
  'overflow-y'?: 'scroll' | 'hidden' | 'auto' | 'visible';
  'width'?: string;
  'height'?: string;
}

// @ts-ignore
export const PaymentChannels = ['MPAY', 'OMISE', 'LINEPAY', 'BANK', 'TPC'];

@Injectable({
  providedIn: 'root'
})
export class AppService {

  constructor(private messageService: MessageService,
              private confirmationService: ConfirmationService,
              private dialogService: DialogService,
              private breadcrumbService: BreadcrumbService,
              private router: Router,
              private logger: NGXLogger,
              private location: Location,
              private clipboardService: ClipboardService,
              private leisureShopRepository: LeisureShopRepository,
              private courseRepository: CourseRepository) {
  }

  onLoginSuccess() {
    log.debug('onLoginSuccess');
    this.courseRepository.fetchNames().subscribe(name => {
      log.info(name);
    });
    // this.leisureShopRepository.fetchNames().subscribe(name => {
    //     log.info(name);
    // });
  }


  navigate(commands: any[], extras?: NavigationExtras) {
    this.router.navigate(commands, extras);
  }

  navigateByUrl(url: string | UrlTree, extras?: NavigationBehaviorOptions) {
    this.router.navigateByUrl(url, extras);
  }

  verify(expression: boolean | any, waringMessage: string) {
    if (!expression) {
      this.toastError(waringMessage);
      throw new Error(waringMessage);
    }
  }

  toastSuccess(detail?: string, cfg?: { life?: number; sticky?: boolean }) {
    this.messageService.add({
      severity: 'success', detail, ...{
        summary: 'Successful',
        life: 3000,
        ...cfg
      }
    });
  }

  toastError(summary: string, error?: any) {
    this.messageService.add({severity: 'error', summary, detail: error ? finestError(error) : error, life: 10000});
  }

  toastAlert(summary: string, detail = '') {
    this.messageService.add({severity: 'warn', summary, detail, life: 10000});
  }

  confirm(action: string, accept: () => void, overrideMessage = false, acceptLabel = 'Yes') {
    this.confirmationService.confirm({
      message: overrideMessage ? action : `Are you sure that you want to ${action}?`,
      header: 'Confirm',
      rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
      icon: 'pi pi-question-circle',
      accept,
      acceptLabel,
    });
  }

  confirmRiskyAction(message: string, accept: () => void) {
    this.confirmationService.confirm({
      message,
      header: 'Proceed with Caution',
      rejectButtonStyleClass: 'p-button-secondary',
      acceptButtonStyleClass: 'p-button-danger p-button-outlined',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes, I am aware of what I\'m doing',
      accept,
    });
  }

  confirmSave(accept: () => void, entityName = '') {
    this.confirmationService.confirm({
      message: `Are you sure that you want to Save ${entityName}?`,
      header: 'Confirm Save',
      icon: 'pi pi-save',
      rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
      accept
    });
  }

  confirmDuplicate(accept: () => void, entityName = '') {
    this.confirmationService.confirm({
      message: `Are you sure that you want to Duplicate ${entityName}?`,
      header: 'Confirm Duplicate',
      icon: 'pi pi-copy',
      rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
      accept
    });
  }

  confirmDelete(entityName = '', accept: () => void) {
    this.confirmationService.confirm({
      message: `Are you sure that you want to Delete ${entityName}?`,
      header: 'Confirm Delete',
      icon: 'pi pi-trash',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
      acceptLabel: 'DELETE',
      accept
    });
  }


  setBreadcrumbItems(items: MenuItem[]) {
    this.breadcrumbService.setItems(items);
  }

  setBreadcrumbPage(name: string) {
    this.breadcrumbService.setLastItem(name);
  }


  showDialog<T>(componentType: Type<DialogBase<T>>, header: string, data?: Partial<T>, style: Style = {}) {
    return this.dialogService.open(componentType, {
        header,
        data,
        style: {
          'min-width': '300px',
          'min-height': '300px',
          'overflow-y': 'scroll',
          ...style
        },
        contentStyle: {overflow: 'visible'},
      }
    );
  }

  showMenuDialog(menus: MenuDialogItem[], header = 'Select an Action', descriptionHtml?: string, actionText?: string): DynamicDialogRef {
    return this.dialogService.open(MenuDialogComponent, {
      header,
      data: {
        menus,
        descriptionHtml,
        actionText
      },
    });
  }

  showInputDialog(header: string, fields: FormlyFieldConfig[], model = {}, width = '400px', height = '400px') {
    return this.dialogService.open(InputDialogComponent, {
      header,
      data: {fields, model},
      width,
      height
      // style: {width}
    });
  }

  // tslint:disable-next-line:max-line-length
  showNumberInputDialog(header: string, label: string, value = 0, options: FormlyTemplateOptions = {}, others: FormlyFieldConfig = {}) {
    const model = {value: 0};
    const fields = [
      formly.requiredNumber('value', label, options, others)
    ];
    return this.showInputDialog(header, fields, model, '500px', '250px');
  }

  // tslint:disable-next-line:max-line-length
  showTextInputDialog(header: string, inputLabel = 'Text', options: FormlyTemplateOptions = {}, others: FormlyFieldConfig = {}) {
    const model = {value: ''};
    const fields = [
      formly.requiredText('value', inputLabel, options, others)
    ];

    return this.showInputDialog(header, fields, model, '600px', '250px');
  }

  copyToClipboard(str: string, toast?: string) {
    this.clipboardService.copy(str);
    this.messageService.add({severity: 'success', summary: 'Copied', detail: str ?? str});
  }

  // Navigate Helper
  navigateToUser(id: string) {
    this.router.navigateByUrl(`/user/${id}`);
  }

  navigateToSlot(id: string) {
    this.router.navigateByUrl(`/slot/${id}`);
  }

  navigateToCourse(id: string) {
    this.router.navigateByUrl(`/course/${id}`);
  }

  navigateToReserve(id = '') {
    this.router.navigateByUrl(`/reserves/${id}`);
  }

  navigateToLeisureReserve(id = '') {
    this.router.navigateByUrl(`/leisure/${id}`);
  }

  navigateToDashBoard() {
    this.router.navigateByUrl(`/dashboard`);
  }

  navigateToVoucher(id: string) {
    this.router.navigateByUrl(`/vouchers/${id}`);
  }

  navigateToTopGolfOrder(id?: string) {
    if (id) {
      this.router.navigateByUrl(`/top-golf/order/${id}`);
    } else {
      this.router.navigateByUrl(`/top-golf/order`);
    }
  }

  navigateToPackage(packageId: string) {
    this.router.navigateByUrl(`/package/?packageId=${packageId}`);
  }

  navigateToPackageList(shopId = '') {
    this.router.navigateByUrl(`/package?shopId=${shopId}`);
  }

  navigateToPriceRuleList(courseId = '') {
    this.router.navigateByUrl(`/price-rule?courseId=${courseId}`);
  }

  navigateToHolidays() {
    this.router.navigateByUrl(`/configuration/holiday`);
  }

  navigateToCourseCalendars(courseId: string | undefined) {
    if (courseId) {
      this.router.navigateByUrl(`/configuration/calendar-setting?courseId=${courseId}`);
    }
  }

  navigateBack() {
    this.location.back();
  }

  redirect(uri: string) {
    this.router.navigate(['/redirector'], {queryParams: {dest: uri}});
  }

  saveToFile(content: string, fileName: string) {
    const blob = new Blob([content], {type: 'text/plain'});
    FileSaver.saveAs(blob, fileName);
  }
}

